import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { I18nGrid } from "@/components/data/I18nGrid";
import {
  CatalogDiagnostic,
  filterDiagnostics,
  useDiagnosticCatalog,
} from "@/hooks/diagnostic/useDiagnosticCatalog";

interface Props {
  selected: CatalogDiagnostic[];
  /** Called with the selection in the order the values were ticked. */
  onChange: (selected: CatalogDiagnostic[]) => void;
}

/** All diagnostics of the database in a scrolling list with tick boxes, filtered while typing. */
export function DiagnosticPicker({ selected, onChange }: Props) {
  const { t } = useTranslation();
  const { data: catalog = [], isLoading, isError } = useDiagnosticCatalog();
  const [query, setQuery] = useState("");
  // Filtering 66,000 names is quick, but the deferred value keeps typing responsive regardless.
  const deferredQuery = useDeferredValue(query);
  const rows = useMemo(() => filterDiagnostics(catalog, deferredQuery), [catalog, deferredQuery]);

  const columns: GridColDef<CatalogDiagnostic>[] = [
    { field: "name", headerName: t("presetEditor.name"), flex: 2, minWidth: 220 },
    { field: "id", headerName: t("presetEditor.id"), flex: 1, minWidth: 180 },
  ];

  const handleSelectionChange = (model: GridRowSelectionModel) => {
    const ids = new Set(model);
    const kept = selected.filter(diagnostic => ids.has(diagnostic.id));
    const keptIds = new Set(kept.map(diagnostic => diagnostic.id));
    const byId = new Map(catalog.map(diagnostic => [diagnostic.id, diagnostic]));
    const added = model.flatMap(id => {
      const diagnostic = byId.get(String(id));
      return diagnostic && !keptIds.has(diagnostic.id) ? [diagnostic] : [];
    });
    onChange([...kept, ...added]);
  };

  return (
    <Box>
      <TextField
        label={t("presetEditor.search")}
        placeholder={t("presetEditor.searchPlaceholder")}
        value={query}
        onChange={event => setQuery(event.target.value)}
        fullWidth
        size={"small"}
        sx={{ mb: 1 }}
        error={isError}
        helperText={
          isError
            ? t("presetEditor.loadFailed")
            : t("presetEditor.matches", { count: rows.length, total: catalog.length })
        }
      />
      <Box sx={{ height: 420 }}>
        <I18nGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          checkboxSelection
          disableRowSelectionOnClick={false}
          keepNonExistentRowsSelected
          rowSelectionModel={selected.map(diagnostic => diagnostic.id)}
          onRowSelectionModelChange={handleSelectionChange}
          density={"compact"}
          hideFooter
          disableColumnMenu
          // Without a filter "select all" would tick all 66,000 diagnostics.
          sx={{ "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": { display: "none" } }}
        />
      </Box>
    </Box>
  );
}
